import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, Radio as RadioIcon, Users, Info, Sparkles, Check, Save, Trash2, BarChart3, Plus, Lock, Unlock, Scale, Recycle, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ErrorBar } from "recharts";
import { sr } from "../../lib/sr";
import { useCurrency } from "../../lib/useCurrency";
import { RadioPlanner } from "../../components/smartreach/RadioPlanner";

const SLIDER_CHANNELS = [
  { key: "social", label: "Paid social · Meta", kind: "digital" },
  { key: "google", label: "Google Search · SEM", kind: "digital" },
  { key: "display", label: "Programmatic · Display", kind: "dsp" },
  { key: "native", label: "Programmatic · Native", kind: "dsp" },
  { key: "video", label: "Online video", kind: "dsp" },
  { key: "ctv", label: "CTV / OTT", kind: "dsp" },
  { key: "audio", label: "Streaming audio", kind: "dsp" },
];
const SLIDER_KEYS = SLIDER_CHANNELS.map((c) => c.key);
const RADIO_TIERS = [0, 1000, 2500, 5000, 7500, 10000];

// Rotating FunAsia radio spot-plan examples (illustrative) shown in the community card.
const RADIO_STORIES = [
  { biz: "a DFW grocery's Diwali sale", plan: "40 spots/week — 15× :30s morning-drive on Radio Caravan, 15× :30s evening on Apna Punjab, and 10× weekend on Sangam Radio", note: "Pairing the weekend spots with a simple \"mention this ad\" offer drove the strongest walk-in lift." },
  { biz: "a South-Asian restaurant's weekend brunch", plan: "32 spots/week — 12× :15s weekday lunch on Radio Vanakkam, 12× :30s Friday–Sunday on Radio Caravan, and 8× weekend on Sangam Radio", note: "Running spots just before mealtimes lifted weekend reservations most." },
  { biz: "a jeweler's wedding-season push", plan: "36 spots/week — 18× :30s evening on Apna Punjab, 10× weekend on Radio Caravan, and 8× :30s midday on Radio Vanakkam", note: "Evening and weekend spots aligned with high-consideration shopping windows." },
];

// Timely FunAsia radio prompts by month (0=Jan) for the DFW South-Asian community.
const SEASONAL_TIPS = {
  0: "Wedding season is in full swing — evening FunAsia spots reach families planning celebrations.",
  1: "Late wedding season plus Valentine's — pair radio with a couples or family offer.",
  2: "Holi and early Eid prep — festive morning-drive spots perform well now.",
  3: "Vaisakhi season — Punjabi-community radio (Apna Punjab) is especially timely.",
  4: "Eid al-Adha window — run greetings plus an offer across the FunAsia network.",
  5: "Summer break — weekend family spots and events drive foot traffic.",
  6: "Summer events season — bundle radio with weekend promotions.",
  7: "Independence Day (India & Pakistan, mid-August) — community-pride spots land well.",
  8: "Navratri prep begins — evening and weekend spots reach festival shoppers.",
  9: "Diwali & Dussehra season — the biggest window; book morning + evening spots early.",
  10: "Diwali and wedding season overlap — premium demand, so lock your spots ahead.",
  11: "Holiday and wedding shopping — weekend spots capture gift and celebration buyers.",
};
const BUDGET_PRESETS = [1000, 3000, 5000];

const nameToKey = (n) => {
  const s = (n || "").toLowerCase();
  if (s.includes("radio")) return "radio";
  if (s.includes("search") || s.includes("google")) return "google";
  if (s.includes("meta") || s.includes("social") || s.includes("facebook") || s.includes("instagram")) return "social";
  if (s.includes("native")) return "native";
  if (s.includes("ctv") || s.includes("ott")) return "ctv";
  if (s.includes("video")) return "video";
  if (s.includes("audio")) return "audio";
  if (s.includes("display")) return "display";
  return null;
};
const radioFloor = (v) => { let t = 0; for (const x of RADIO_TIERS) if (x <= v) t = x; return t; };

export default function MediaMix() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const planId = params.get("plan");
  const [plan, setPlan] = useState(null);
  const [alloc, setAlloc] = useState({});
  const [forecast, setForecast] = useState(null);
  const [fLoading, setFLoading] = useState(false);
  const [community, setCommunity] = useState(null);
  const [wantPartner, setWantPartner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState("");
  const [savingScenario, setSavingScenario] = useState(false);
  const [compareOn, setCompareOn] = useState(false);
  const [baselineAlloc, setBaselineAlloc] = useState(null);
  const [scenariosLoaded, setScenariosLoaded] = useState(false);
  const [radioPkgs, setRadioPkgs] = useState([]);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [allowOver, setAllowOver] = useState(false);
  const [shown, setShown] = useState([]);
  const [locked, setLocked] = useState([]);
  const [budget, setBudget] = useState(3000);
  const [dailySpend, setDailySpend] = useState(100);
  const [durationDays, setDurationDays] = useState(30);
  const [conquesting, setConquesting] = useState(false);
  const [reclaim, setReclaim] = useState(null);
  const [reclaiming, setReclaiming] = useState(false);
  const highlightReclaim = params.get("reclaim") === "1";
  const cur = useCurrency();
  const { money, toLocal, toUSD, symbol, isLocal } = cur;
  const [storyIdx, setStoryIdx] = useState(() => Math.floor(Math.random() * RADIO_STORIES.length));
  const autoSavedRef = useRef(false);

  const CH_COLORS = { radio: "#C9A227", social: "#2E6BE6", google: "#1FA971", display: "#7A5AF8", native: "#E8734A", video: "#E0459B", ctv: "#0EA5B7", audio: "#8B5E34" };

  useEffect(() => {
    (async () => {
      if (!planId) { navigate("/app/onboarding"); return; }
      try {
        const r = await sr.getPlan(planId);
        const p = r.growth_plan;
        setPlan(p);
        const b = p.monthly_budget || 3000;
        setBudget(b);
        setDailySpend(p.daily_spend || Math.max(1, Math.round(b / 30)));
        setDurationDays(p.duration_days || 30);
        setConquesting(!!p.conquesting);
        const mix = p.plan?.channel_mix || [];
        const map = { radio: 0 }; SLIDER_CHANNELS.forEach((c) => (map[c.key] = 0));
        const picked = Array.isArray(p.channels) ? p.channels : [];
        const pickedDigital = picked.length ? SLIDER_KEYS.filter((k) => picked.includes(k)) : SLIDER_KEYS.slice();
        if (p.media_mix?.allocations) {
          Object.assign(map, p.media_mix.allocations);
        } else {
          let assigned = 0;
          mix.forEach((m) => { const k = nameToKey(m.channel); if (k && k !== "radio") { const amt = Math.round(b * (m.budget_pct || 0) / 100); map[k] += amt; assigned += amt; } });
          const radioShare = mix.filter((m) => nameToKey(m.channel) === "radio").reduce((a, m) => a + Math.round(b * (m.budget_pct || 0) / 100), 0);
          map.radio = radioFloor(radioShare);
          assigned += map.radio;
          const fallbackKey = pickedDigital[0] || "display";
          if (assigned <= 0) { map[fallbackKey] = b; }
          else if (assigned < b) {
            const largest = SLIDER_KEYS.filter((k) => (map[k] || 0) > 0).sort((x, y) => (map[y] || 0) - (map[x] || 0))[0] || fallbackKey;
            map[largest] += b - assigned;
          }
        }
        map.radio = 0; // radio is now a separate geo-gated booking, not part of the digital budget
        setAlloc(map);
        setBaselineAlloc(map);
        // Which digital sliders to show: the owner-picked channels + any the AI already funded.
        let sh = SLIDER_KEYS.filter((k) => picked.includes(k) || (Number(map[k]) || 0) > 0);
        if (!sh.length) sh = picked.length ? SLIDER_KEYS.filter((k) => picked.includes(k)) : SLIDER_KEYS.slice();
        setShown(sh);
      } catch { toast.error("Could not load your plan"); }
      try { const c = await sr.audienceCommunity(); setCommunity(c); } catch { /* ignore */ }
      try { const r = await sr.radioPackages(true); setRadioPkgs(r.packages || []); } catch { /* ignore */ }
    })();
  }, [planId, navigate]);

  const loadReclaim = useCallback(() => {
    if (planId) sr.getBudgetReclaim(planId).then(setReclaim).catch(() => {});
  }, [planId]);
  useEffect(() => { loadReclaim(); }, [loadReclaim]);

  const autoReclaim = async () => {
    setReclaiming(true);
    try {
      const r = await sr.applyBudgetReclaim(planId);
      const a = r.media_mix?.allocations || {};
      const map = { radio: 0 }; SLIDER_CHANNELS.forEach((c) => (map[c.key] = 0));
      Object.assign(map, a); map.radio = 0;
      setAlloc(map);
      setBudget(r.reclaim?.total_budget || budget);
      setShown((s) => Array.from(new Set([...s, ...Object.keys(a).filter((k) => SLIDER_KEYS.includes(k) && Number(a[k]) > 0)])));
      setReclaim(r.reclaim || null);
      toast.success("Reclaimed unused budget across your active channels");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reclaim budget"); }
    finally { setReclaiming(false); }
  };

  const manualReclaim = () => {
    if (!reclaim) return;
    const strandedKeys = (reclaim.channels || []).filter((c) => c.stranded).map((c) => c.channel);
    setAlloc((a) => { const next = { ...a }; strandedKeys.forEach((k) => { next[k] = 0; }); return next; });
    setBudget(reclaim.total_budget || budget);
    setShown((s) => Array.from(new Set([...s, ...strandedKeys.filter((k) => SLIDER_KEYS.includes(k))])));
    toast.message("Freed up the unused budget — drag the sliders to place it, then Review my plan to save");
  };

  const total = useMemo(() => Object.values(alloc).reduce((a, v) => a + (Number(v) || 0), 0), [alloc]);
  const remaining = budget - total;
  const radioPkg = useMemo(() => (radioPkgs || []).find((p) => p.monthly_budget === Number(alloc.radio)) || null, [radioPkgs, alloc.radio]);

  const runForecast = useCallback(async (a) => {
    const active = Object.fromEntries(Object.entries(a).filter(([, v]) => Number(v) > 0));
    if (!Object.keys(active).length) { setForecast(null); return; }
    setFLoading(true);
    try { setForecast(await sr.mediaMixForecast(active, planId)); } catch { /* ignore */ } finally { setFLoading(false); }
  }, [planId]);

  useEffect(() => {
    if (!plan) return;
    const t = setTimeout(() => runForecast(alloc), 400);
    return () => clearTimeout(t);
  }, [alloc, plan, runForecast]);

  // Add a channel freely: pull the shortfall from the largest funded channels so the total stays locked
  // at the budget (unless the owner turns on "allow over budget"). No more "not enough left" dead-ends.
  const rebalanceAlloc = (a, key, rawTarget) => {
    const target = Math.max(0, Math.round(Number(rawTarget) || 0));
    if (allowOver) return { ...a, [key]: target };
    const next = { ...a, [key]: target };
    const sumOthers = Object.entries(next).reduce((s, [k, v]) => (k === key ? s : s + (Number(v) || 0)), 0);
    let over = sumOthers + target - budget;
    if (over > 0) {
      const donors = Object.entries(next)
        .filter(([k, v]) => k !== key && !locked.includes(k) && (Number(v) || 0) > 0)
        .sort((x, y) => (Number(y[1]) || 0) - (Number(x[1]) || 0));
      for (const [k] of donors) {
        if (over <= 0) break;
        const cur = Number(next[k]) || 0;
        const take = Math.min(cur, over);
        next[k] = cur - take; over -= take;
      }
      if (over > 0) next[key] = Math.max(0, target - over);
    }
    return next;
  };

  const setChannel = (key, val) => setAlloc((a) => rebalanceAlloc(a, key, val));
  const setRadioTier = (tier) => setAlloc((a) => rebalanceAlloc(a, "radio", tier));
  const addChannel = (key) => setShown((s) => (s.includes(key) ? s : [...s, key]));
  const toggleLock = (key) => setLocked((l) => (l.includes(key) ? l.filter((x) => x !== key) : [...l, key]));

  // Bump the monthly budget on the planner: keep radio + locked amounts, scale the unlocked digital
  // channels to fill the new budget so channels and the reach forecast update live.
  const changeBudget = (val) => {
    const nb = Math.max(0, Math.round(Number(val) || 0));
    setBudget(nb);
    setAlloc((a) => {
      const radioAmt = Number(a.radio) || 0;
      const digitalKeys = Object.keys(a).filter((k) => k !== "radio");
      const lockedSum = digitalKeys.filter((k) => locked.includes(k)).reduce((s, k) => s + (Number(a[k]) || 0), 0);
      const unlocked = digitalKeys.filter((k) => !locked.includes(k));
      const oldUnlocked = unlocked.reduce((s, k) => s + (Number(a[k]) || 0), 0);
      let pool = nb - radioAmt - lockedSum;
      if (pool < 0) pool = 0;
      const next = { ...a };
      if (oldUnlocked > 0) {
        const factor = pool / oldUnlocked;
        unlocked.forEach((k) => (next[k] = Math.round((Number(a[k]) || 0) * factor / 50) * 50));
      } else if (unlocked.length) {
        const per = Math.floor(pool / unlocked.length / 50) * 50;
        unlocked.forEach((k) => (next[k] = per));
      }
      // Absorb any rounding remainder on the first unlocked channel so the total lands exactly on budget.
      if (unlocked.length) {
        const used = unlocked.reduce((s, k) => s + (Number(next[k]) || 0), 0);
        next[unlocked[0]] = Math.max(0, (Number(next[unlocked[0]]) || 0) + (pool - used));
      }
      return next;
    });
  };

  const changeDaily = (val) => {
    const d = Math.max(0, Math.round(Number(val) || 0));
    setDailySpend(d);
    changeBudget(d * 30);
  };

  const toggleConquesting = async (on) => {
    setConquesting(on);
    try { await sr.setPlanTargeting(planId, { conquesting: on }); }
    catch { toast.error("Could not update conquesting"); }
  };

  const resetToAI = () => {
    if (!baselineAlloc) return;
    setAlloc({ ...baselineAlloc });
    setBudget(plan?.monthly_budget || 3000);
    setLocked([]);
    setAllowOver(false);
    const picked = Array.isArray(plan?.channels) ? plan.channels : [];
    let sh = SLIDER_KEYS.filter((k) => picked.includes(k) || (Number(baselineAlloc[k]) || 0) > 0);
    if (!sh.length) sh = picked.length ? SLIDER_KEYS.filter((k) => picked.includes(k)) : SLIDER_KEYS.slice();
    setShown(sh);
    toast.success("Reset to the AI's recommended mix");
  };

  // One-tap balanced starting mix: split the budget evenly across the shown digital channels,
  // keeping any radio spend and any locked channel amounts fixed.
  const evenSplit = () => {
    setAlloc((a) => {
      const unlocked = shown.filter((k) => !locked.includes(k));
      if (!unlocked.length) { toast.error("Unlock a channel to split across it"); return a; }
      const radioAmt = Number(a.radio) || 0;
      const lockedSum = shown.filter((k) => locked.includes(k)).reduce((s, k) => s + (Number(a[k]) || 0), 0);
      let pool = budget - radioAmt - lockedSum;
      if (pool < 0) pool = 0;
      const per = Math.floor(pool / unlocked.length / 50) * 50;
      const next = { ...a };
      unlocked.forEach((k) => (next[k] = per));
      const rem = pool - per * unlocked.length;
      if (rem > 0) next[unlocked[0]] += rem;
      return next;
    });
    toast.success("Split evenly across your channels");
  };

  const optInCommunity = async () => {
    try {
      await sr.communityInterest("south_asian", wantPartner);
      setCommunity((c) => (c ? { ...c, already_interested: true } : c));
      toast.success(wantPartner ? "Great — we'll arrange a partner introduction" : "Saved");
    } catch { toast.error("Could not save your choice"); }
  };

  const approveBookRadio = async () => {
    if (!radioPkg) return;
    setBooking(true);
    try {
      await sr.bookRadioPackage(radioPkg.key, { plan_id: planId, weeks: 4, bundle_eligible: true });
      setBooked(true);
      toast.success("Approved — your FunAsia package is in the radio ops booking queue");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not book this package"); }
    finally { setBooking(false); }
  };

  const reviewPlan = async () => {
    setSaving(true);
    try { await sr.saveMediaMix(planId, alloc); navigate(`/app/growth-plan?id=${planId}`); }
    catch { toast.error("Could not save your mix"); setSaving(false); }
  };

  const loadScenarios = useCallback(() => {
    if (!planId) return;
    sr.listMixScenarios(planId).then((r) => { setScenarios(r.scenarios || []); setScenariosLoaded(true); }).catch(() => setScenariosLoaded(true));
  }, [planId]);
  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  useEffect(() => {
    if (!scenariosLoaded || autoSavedRef.current || !baselineAlloc) return;
    if (scenarios.length > 0) { autoSavedRef.current = true; return; }
    const active = Object.fromEntries(Object.entries(baselineAlloc).filter(([, v]) => Number(v) > 0));
    if (!Object.keys(active).length) return;
    autoSavedRef.current = true;
    sr.saveMixScenario(planId, "AI recommended", active, "ai").then(() => loadScenarios()).catch(() => {});
  }, [scenariosLoaded, scenarios.length, baselineAlloc, planId, loadScenarios]);

  const saveScenario = async () => {
    const active = Object.fromEntries(Object.entries(alloc).filter(([, v]) => Number(v) > 0));
    if (!Object.keys(active).length) { toast.error("Add some budget first"); return; }
    setSavingScenario(true);
    try {
      await sr.saveMixScenario(planId, scenarioName || `Mix ${scenarios.length + 1}`, active);
      setScenarioName(""); toast.success("Mix saved"); loadScenarios();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save mix"); } finally { setSavingScenario(false); }
  };
  const loadScenario = (s) => { setAlloc({ radio: 0, ...Object.fromEntries(SLIDER_KEYS.map((k) => [k, 0])), ...s.allocations }); setShown((sh) => Array.from(new Set([...sh, ...Object.keys(s.allocations || {}).filter((k) => SLIDER_KEYS.includes(k))]))); toast.success(`Loaded "${s.name}"`); };
  const deleteScenario = async (s) => { try { await sr.deleteMixScenario(planId, s.id); loadScenarios(); } catch { toast.error("Could not delete"); } };

  const chartData = useMemo(() => (forecast?.channels || [])
    .filter((c) => c.unit === "impressions" && Number(c.value) > 0)
    .map((c) => {
      const lo = Number(c.value_low ?? c.value);
      const hi = Number(c.value_high ?? c.value);
      return { name: (c.channel === "radio" ? "Radio" : c.channel.charAt(0).toUpperCase() + c.channel.slice(1)),
               reach: c.value, key: c.channel, err: [Math.max(0, c.value - lo), Math.max(0, hi - c.value)] };
    }), [forecast]);
  const googleClicks = useMemo(() => (forecast?.channels || []).find((c) => c.unit === "clicks"), [forecast]);

  const fByChannel = useMemo(() => {
    const m = {}; (forecast?.channels || []).forEach((c) => (m[c.channel] = c)); return m;
  }, [forecast]);

  if (!plan) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;

  const hiddenChannels = SLIDER_CHANNELS.filter((c) => !shown.includes(c.key));

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 4px 40px" }}>
      <h1 style={{ color: "var(--navy)", fontSize: 26, marginBottom: 4 }}>Shape your media mix</h1>
      <p className="sr-muted" style={{ marginBottom: 16 }}>Drag each channel to see the potential reach. Add or remove channels freely — your budget of <strong>{money(budget)}</strong> rebalances automatically.</p>

      <div className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", position: "sticky", top: 8, zIndex: 5, marginBottom: 14 }} data-testid="mm-budget-bar">
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <strong style={{ color: "var(--navy)" }}>Allocated {money(total)}</strong>
          <span className="sr-muted">of</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <span className="sr-muted">{symbol}</span>
            <input type="number" min={0} step={100} value={toLocal(budget)} onChange={(e) => changeBudget(toUSD(e.target.value))} data-testid="mm-budget-input"
              style={{ width: 100, padding: "5px 7px", border: "1px solid var(--border)", borderRadius: 7, fontWeight: 700, color: "var(--navy)" }} />
            <span className="sr-muted">/mo</span>
          </span>
          <span style={{ display: "inline-flex", gap: 4 }} data-testid="mm-budget-presets">
            {BUDGET_PRESETS.map((amt) => (
              <button key={amt} className={`sr-btn sr-btn-sm ${Number(budget) === amt ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => changeBudget(amt)} data-testid={`mm-budget-preset-${amt}`}>{symbol}{Math.round(amt * cur.fx / 1000)}k</button>
            ))}
          </span>
          {isLocal && (
            <span className="sr-badge" style={{ background: "#EAF2FF", color: "var(--blue)" }} data-testid="mm-budget-usd">
              ≈ ${Math.round(budget).toLocaleString()} USD billed
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {remaining < 0
            ? <span className="sr-badge" style={{ background: "#FBE3E1", color: "#C0362C" }} data-testid="mm-over-budget">Over budget by {money(-remaining)}</span>
            : <span className={remaining > 0 ? "sr-badge sr-badge-amber" : "sr-badge sr-badge-green"} data-testid="mm-remaining">{remaining > 0 ? `${money(remaining)} left to allocate` : "Fully allocated"}</span>}
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={resetToAI} data-testid="mm-reset-ai"><Sparkles size={13} /> Reset to AI mix</button>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={evenSplit} data-testid="mm-even-split"><Scale size={13} /> Split evenly</button>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12, color: "var(--muted)", cursor: "pointer" }}>
            <input type="checkbox" checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} data-testid="mm-allow-over" /> Allow over budget
          </label>
        </div>
      </div>

      <div className="sr-card" style={{ marginBottom: 14, display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }} data-testid="mm-daily-card">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className="sr-muted">{symbol}</span>
          <input type="number" min={1} value={toLocal(dailySpend)} onChange={(e) => changeDaily(toUSD(e.target.value))} data-testid="mm-daily-input"
            style={{ width: 88, padding: "5px 7px", border: "1px solid var(--border)", borderRadius: 7, fontWeight: 700, color: "var(--navy)" }} />
          <span className="sr-muted">/day</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="number" min={1} value={durationDays} onChange={(e) => setDurationDays(Math.max(1, Math.round(Number(e.target.value) || 0)))} data-testid="mm-duration-input"
            style={{ width: 64, padding: "5px 7px", border: "1px solid var(--border)", borderRadius: 7, fontWeight: 700, color: "var(--navy)" }} />
          <span className="sr-muted">days</span>
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <span style={{ fontSize: 14, color: "var(--navy)" }} data-testid="mm-daily-summary">
            ≈ <strong>{money(Number(dailySpend) * Number(durationDays))}</strong> total over {durationDays} days
            {conquesting && <> · <span style={{ color: "#B8791B", fontWeight: 700 }}>conquesting on</span></>}
          </span>
          <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }} data-testid="mm-duration-rec">For the best results, we recommend running for at least 30 days.</div>
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", maxWidth: 320 }} data-testid="mm-conquesting-label">
          <input type="checkbox" checked={conquesting} onChange={(e) => toggleConquesting(e.target.checked)} data-testid="mm-conquesting" style={{ marginTop: 3 }} />
          <span>
            <strong style={{ color: "var(--navy)", fontSize: 13 }}>Competitor Conquesting</strong>
            <div className="sr-muted" style={{ fontSize: 12 }}>Target audiences around your competitors to win their customers.</div>
          </span>
        </label>
      </div>

      {reclaim?.has_stranded && (
        <div className="sr-card" data-testid="mm-reclaim-banner"
          style={{ marginBottom: 14, background: "#FFF8EC", border: `1px solid ${highlightReclaim ? "#D98A00" : "#F3D9A4"}`,
            boxShadow: highlightReclaim ? "0 0 0 3px rgba(217,138,0,0.15)" : "none" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
            <AlertTriangle size={18} style={{ color: "#B8791B", flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <strong style={{ color: "var(--navy)" }} data-testid="mm-reclaim-amount">{money(reclaim.stranded_total)} unused budget</strong>
              <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 3 }} data-testid="mm-reclaim-breakdown">
                {reclaim.stranded_from_no_creative >= 1 && (
                  <span>{money(reclaim.stranded_from_no_creative)} sits on channels with no ad uploaded yet. </span>
                )}
                {reclaim.stranded_from_unallocated >= 1 && (
                  <span>{money(reclaim.stranded_from_unallocated)} isn't allocated to any active channel. </span>
                )}
                Reclaim it to your {reclaim.recipients_use_fallback ? "active channels" : "channels that already have an ad ready"}.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={autoReclaim} disabled={reclaiming || !reclaim.can_reclaim} data-testid="mm-reclaim-auto">
              {reclaiming ? <Loader2 size={13} className="sr-spin" /> : <><Recycle size={13} /> Reclaim &amp; reallocate</>}
            </button>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={manualReclaim} disabled={reclaiming} data-testid="mm-reclaim-manual">
              <Scale size={13} /> Adjust manually
            </button>
            {!reclaim.can_reclaim && (
              <span className="sr-muted" style={{ fontSize: 11.5, alignSelf: "center" }} data-testid="mm-reclaim-blocked">Add an ad to a channel first, so there's somewhere to move this budget.</span>
            )}
          </div>
        </div>
      )}

      {SLIDER_CHANNELS.filter((c) => shown.includes(c.key)).map((c) => {
        const f = fByChannel[c.key];
        return (
          <div className="sr-card" style={{ marginBottom: 10 }} key={c.key} data-testid={`mm-ch-${c.key}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong style={{ color: "var(--navy)" }}>{c.label}</strong>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span className="sr-badge sr-badge-gray" data-testid={`mm-amt-${c.key}`}>{money(alloc[c.key])}</span>
                <button title={locked.includes(c.key) ? "Unlock this channel" : "Lock this amount"} onClick={() => toggleLock(c.key)} data-testid={`mm-lock-${c.key}`} style={{ background: "none", border: "none", cursor: "pointer", color: locked.includes(c.key) ? "var(--gold)" : "var(--muted)", padding: 2, display: "inline-flex" }}>
                  {locked.includes(c.key) ? <Lock size={15} /> : <Unlock size={15} />}
                </button>
              </div>
            </div>
            <input type="range" min={0} max={budget} step={50} value={alloc[c.key] || 0} disabled={locked.includes(c.key)}
              onChange={(e) => setChannel(c.key, e.target.value)} style={{ width: "100%", marginTop: 8, accentColor: "var(--blue)", opacity: locked.includes(c.key) ? 0.5 : 1 }} data-testid={`mm-slider-${c.key}`} />
            <div className="sr-muted" style={{ fontSize: 13, display: "flex", justifyContent: "space-between" }}>
              <span data-testid={`mm-reach-${c.key}`}>{Number(alloc[c.key]) > 0 ? (f ? f.readable : "…") : "Not in the mix"}</span>
              <span>{f && Number(alloc[c.key]) > 0 ? "In the mix" : "drag to add"}</span>
            </div>
          </div>
        );
      })}

      {/* Add another channel */}
      {hiddenChannels.length > 0 && (
        <div className="sr-card" style={{ marginBottom: 14, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }} data-testid="mm-add-channel">
          <span className="sr-muted" style={{ fontSize: 13 }}>Add a channel:</span>
          {hiddenChannels.map((c) => (
            <button key={c.key} className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => addChannel(c.key)} data-testid={`mm-add-${c.key}`}>
              <Plus size={13} /> {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Reach summary */}
      <div className="sr-card" style={{ marginTop: 6, marginBottom: 14, background: "var(--blue-light)", border: "1px solid var(--blue)" }} data-testid="mm-summary">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ color: "var(--navy)" }}>Potential reach {fLoading && <Loader2 size={13} className="sr-spin" />}</strong>
          <span className="sr-muted" style={{ fontSize: 13 }} data-testid="mm-allocated-total">Allocated {money(forecast?.totals?.customer_allocation)}</span>
        </div>
        {forecast?.totals?.gross_impression_opportunities > 0 && (
          <div data-testid="mm-gross-impressions">
            <div style={{ fontSize: 22, color: "var(--navy)", fontWeight: 700, marginTop: 4 }}>~{forecast.totals.gross_impression_opportunities.toLocaleString()} gross impression opportunities/mo</div>
            {forecast.totals.gross_impressions_high > 0 && (
              <div className="sr-muted" style={{ fontSize: 12 }} data-testid="mm-reach-range">Planning band: {forecast.totals.gross_impressions_low.toLocaleString()} – {forecast.totals.gross_impressions_high.toLocaleString()} (low–high)</div>
            )}
          </div>
        )}
        {chartData.length > 0 && (
          <>
            <div style={{ height: 200, marginTop: 10 }} data-testid="mm-reach-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 12, right: 8, left: 8, bottom: 4 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} height={20} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} tick={{ fontSize: 10 }} width={34} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => [`${Number(v).toLocaleString()} impressions (middle)`, "Reach"]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="reach" radius={[6, 6, 0, 0]} isAnimationActive>
                    {chartData.map((d) => <Cell key={d.key} fill={CH_COLORS[d.key] || "#2E6BE6"} />)}
                    <ErrorBar dataKey="err" width={5} strokeWidth={1.5} stroke="#1f2a44" direction="y" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="sr-muted" style={{ fontSize: 11, textAlign: "center", marginTop: 4 }}>Bars show the middle estimate; whiskers show the low–high planning band.</div>
          </>
        )}
        {googleClicks && Number(googleClicks.value) > 0 && (
          <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }} data-testid="mm-google-clicks">Google Search: ~{Number(googleClicks.value).toLocaleString()} clicks/mo (shown separately from impressions)</div>
        )}
        {(forecast?.disclaimers || []).map((d, i) => (
          <div key={i} className="sr-muted" style={{ fontSize: 11, marginTop: 4, display: "flex", gap: 5 }}><Info size={11} style={{ flexShrink: 0, marginTop: 2 }} />{d}</div>
        ))}
      </div>

      {/* Radio — geo-gated (FunAsia in DFW, Big FM/LUV/Talk in Dubai) with per-station selection */}
      <RadioPlanner planId={planId} />

      {/* Save & compare named mixes */}
      <div className="sr-card" style={{ marginBottom: 16 }} data-testid="mm-scenarios">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <strong style={{ color: "var(--navy)" }}>Saved mixes</strong>
          <div style={{ display: "flex", gap: 6 }}>
            <input className="sr-input sr-input-sm" style={{ width: 150 }} placeholder="Name this mix" value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} data-testid="mm-scenario-name" />
            <button className="sr-btn sr-btn-sm" onClick={saveScenario} disabled={savingScenario} data-testid="mm-scenario-save">{savingScenario ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save</>}</button>
            {scenarios.length >= 2 && <button className={`sr-btn sr-btn-sm ${compareOn ? "sr-btn-primary" : ""}`} onClick={() => setCompareOn((v) => !v)} data-testid="mm-scenario-compare"><BarChart3 size={13} /> Compare</button>}
          </div>
        </div>
        {scenarios.length === 0 ? (
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>Save a couple of budget mixes to compare their reach side by side before you approve.</p>
        ) : compareOn ? (
          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="mm-compare-table">
              <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}>
                <th style={{ padding: "6px 8px" }}>Mix</th><th style={{ padding: "6px 8px" }}>Budget</th><th style={{ padding: "6px 8px" }}>Gross impressions</th><th /></tr></thead>
              <tbody>
                {scenarios.map((s) => (
                  <tr key={s.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "6px 8px", fontWeight: 600 }}>{s.name}</td>
                    <td style={{ padding: "6px 8px" }}>{money(s.totals?.customer_allocation)}</td>
                    <td style={{ padding: "6px 8px" }}>~{Number(s.totals?.gross_impression_opportunities || 0).toLocaleString()}</td>
                    <td style={{ padding: "6px 8px" }}><button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => loadScenario(s)}>Load</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            {scenarios.map((s) => (
              <div key={s.id} className={`sr-badge ${s.source === "ai" ? "sr-badge-green" : "sr-badge-gray"}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid={`mm-scenario-${s.id}`}>
                <span style={{ cursor: "pointer" }} onClick={() => loadScenario(s)}>{s.source === "ai" ? "★ " : ""}{s.name} · {money(s.totals?.customer_allocation)} · ~{Number(s.totals?.gross_impression_opportunities || 0).toLocaleString()} imp</span>
                {s.source !== "ai" && <Trash2 size={12} style={{ cursor: "pointer" }} onClick={() => deleteScenario(s)} data-testid={`mm-scenario-del-${s.id}`} />}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button className="sr-btn sr-btn-ghost" onClick={() => navigate(`/app/growth-plan?id=${planId}`)} data-testid="mm-skip">Skip</button>
        <button className="sr-btn sr-btn-primary" onClick={reviewPlan} disabled={saving} data-testid="mm-review">
          {saving ? <Loader2 size={15} className="sr-spin" /> : <>Review my plan <ArrowRight size={15} /></>}
        </button>
      </div>
    </div>
  );
}
